import { FolderOpen } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { match } from "ts-pattern";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { useAgentStream } from "@/hooks/agent/useAgentStream";
import { useThreadCosts } from "@/hooks/agent/useThreadCosts";
import { cn } from "@/lib/utils";
import { AgentMessage } from "@/models/agent/message";
import { useAgentStore } from "@/stores/agent/useAgentStore";
import AgentChatInput from "./AgentChatInput";
import { Button } from "./Button";
import MessageListUI from "./chatMessages/MessageListUI";
import { FileBrowserModal } from "./FileBrowserModal";
import TaskProgressDisplay from "./TaskProgressDisplay";
import { ToolPanel } from "./ToolPanel";

interface AgentChatContainerUIProps {
  className?: string;
}

const AgentChatContainerUI: React.FC<AgentChatContainerUIProps> = ({ className }) => {
  const { threadId } = useParams();
  const pushOptimisticMessage = useAgentStore((state) => state.pushOptimisticMessage);
  const setLatestStatusMessage = useAgentStore((state) => state.setLatestStatusMessage);
  const setCurrentAgentRunId = useAgentStore((state) => state.setCurrentAgentRunId);
  const latestTaskList = useAgentStore((state) => state.latestTaskList);
  const isOpenToolPanel = useAgentStore((state) => state.isOpenToolPanel);
  const isToolPanelExpanded = useAgentStore((state) => state.isToolPanelExpanded);
  const messageListRef = useRef<HTMLDivElement>(null);
  const [isOpenFileBrowser, setIsOpenFileBrowser] = useState(false);
  const { mutate: mutateThreadCosts } = useThreadCosts(threadId || null);

  const onMessage = useCallback((message: AgentMessage) => {
    match(message.role)
      .with("assistant", "assistant_response_end", "tool_call", "tool_response", () => {
        // サブエージェントの最終出力は表示しない
        if (message.isSubAgentFinalResponse) return;

        if (message.isMainAgentFinalResponse) {
          setCurrentAgentRunId(null);
        }

        pushOptimisticMessage(message);

        // status以外のメッセージ受信時にコスト更新
        mutateThreadCosts();
      })
      .with("status", () => {
        setLatestStatusMessage(message.content.message === "Completing agent execution" ? null : message);
      })
      .with("user", () => {}).otherwise(() => {
        console.warn("Unknown message:", message);
      });
  }, [pushOptimisticMessage, setLatestStatusMessage, setCurrentAgentRunId, mutateThreadCosts]);

  // SSEストリーミング
  useAgentStream(threadId, onMessage);

  return (
    <div className={cn("flex h-full", className)}>
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col justify-center relative">
        <div className="flex-1 flex flex-col w-full h-full">
          {/* Messages Area */}
          <ScrollArea className="h-full" viewportRef={messageListRef}>
            <div className="max-w-[768px] px-4 mx-auto">
              {/* メッセージリスト */}
              {threadId && <MessageListUI threadId={threadId} scrollRef={messageListRef} />}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>

          {/* Task Progress - inputボックスの上 */}
          {threadId && latestTaskList && latestTaskList.length > 0 && (
            <div className="px-4 pb-2 max-w-[768px] mx-auto w-full">
              <TaskProgressDisplay tasks={latestTaskList} />
            </div>
          )}

          {/* Input Area */}
          <div className="px-4 pb-4 max-w-[768px] mx-auto w-full">
            <AgentChatInput />
          </div>
        </div>
      </div>

      {/* Utility Icons */}
      {threadId && (
        <div
          className={cn(
            "fixed top-28 right-3 z-50 transition-all duration-300",
            isOpenToolPanel ? "opacity-0 pointer-events-none" : "opacity-100",
          )}
        >
          <Button
            onClick={() => setIsOpenFileBrowser(true)}
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
          >
            <FolderOpen className="h-4 w-4 text-gray-600" />
          </Button>
        </div>
      )}

      {/* Tool Panel */}
      <div
        className={cn(
          "my-5 overflow-hidden transition-all duration-150 ease-in-out",
          isOpenToolPanel && !isToolPanelExpanded
            ? "max-[1440px]:w-[420px] max-[1680px]:w-[560px] w-[640px] max-w-[640px] max-h-full opacity-100"
            : "max-w-0 max-h-0 opacity-0",
        )}
      >
        <ToolPanel />
      </div>

      {/* File Browser Modal */}
      {threadId && (
        <FileBrowserModal
          open={isOpenFileBrowser}
          onOpenChange={setIsOpenFileBrowser}
          threadId={threadId}
        />
      )}
    </div>
  );
};

export default AgentChatContainerUI;
